import { get, send } from "../api.js";

export interface LessonBlockTransferPreview {
  source_lesson_block_id: string;
  portal_student_id: string;
  student_name: string | null;
  source_provider_id: number;
  source_service_id: number;
  source_product_id: string;
  source_classification: string;
  source_balance: number;
  destination_provider_id: number;
  destination_service_id: number;
  destination_product_id: string;
  transferred_credits: number;
  can_apply: boolean;
  blockers: string[];
  coupon_consequences: string[];
  history_statement: string;
  future_appointments_statement: string;
  preview_token: string;
}

export type LessonBlockTransferState =
  | "coupon_reconciliation_pending"
  | "hub_assignment_pending"
  | "hub_assignment_failed"
  | "completed"
  | "requires_attention";

export interface LessonBlockTransferOperation {
  id: string;
  idempotency_key: string;
  source_lesson_block_id: string;
  destination_lesson_block_id: string | null;
  source_product_id: string;
  destination_product_id: string;
  portal_student_id: string;
  source_provider_id: number;
  source_service_id: number;
  destination_provider_id: number;
  destination_service_id: number;
  transferred_credits: number;
  source_classification: string;
  reason: string;
  preview_fingerprint: string;
  state: LessonBlockTransferState;
  last_error: string | null;
  source_debit_transaction_id: string | null;
  destination_grant_transaction_id: string | null;
  source_coupon_id: string | null;
  source_coupon_previous_limit: number | null;
  source_coupon_target_limit: number | null;
  destination_coupon_id: string | null;
  acted_by_staff_id: string | null;
  actor_role: string;
  authority_context: string;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

const root = "/staff-api/api/v1/lesson-block-transfers";

export const lessonBlockTransfer = {
  preview: (
    sourceLessonBlockId: string,
    destinationProviderId: number,
    destinationProductId: string,
  ) =>
    send<LessonBlockTransferPreview>(
      "POST",
      `${root}/${encodeURIComponent(sourceLessonBlockId)}/preview`,
      {
        destination_provider_id: destinationProviderId,
        destination_product_id: destinationProductId,
      },
    ),

  apply: (
    sourceLessonBlockId: string,
    input: {
      destination_provider_id: number;
      destination_product_id: string;
      preview_token: string;
      reason: string;
    },
    key: string,
  ) =>
    send<LessonBlockTransferOperation>(
      "POST",
      `${root}/${encodeURIComponent(sourceLessonBlockId)}/apply`,
      input,
      { "Idempotency-Key": key },
    ),

  unresolved: (portalStudentId: string) =>
    get<LessonBlockTransferOperation[]>(`${root}/operations`, {
      portal_student_id: portalStudentId,
      unresolved_only: "true",
    }),

  retry: (operationId: string) =>
    send<LessonBlockTransferOperation>(
      "POST",
      `${root}/operations/${encodeURIComponent(operationId)}/retry`,
    ),

  recordHubReconciliation: (
    operationId: string,
    succeeded: boolean,
    error?: string,
  ) =>
    send<LessonBlockTransferOperation>(
      "POST",
      `${root}/operations/${encodeURIComponent(operationId)}/hub-reconciliation`,
      { succeeded, ...(succeeded ? {} : { error: error ?? "Hub tutor assignment failed" }) },
    ),
};
